import type {
  ChartGeometry,
  ChartSeries,
  ChartSpec,
} from './types';

const MARKS = new Set(['line', 'bar', 'area', 'scatter', 'candlestick']);

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function numericFieldExists(spec: ChartSpec, key: string): boolean {
  return spec.data.some((row) => typeof row[key] === 'number');
}

export function validateChartSpec(value: unknown): string[] {
  if (!value || typeof value !== 'object') return ['Chart payload is not an object.'];
  const spec = value as Partial<ChartSpec>;
  const errors: string[] = [];

  if (spec.kind !== 'timeseries' && spec.kind !== 'category') {
    errors.push('Chart kind must be timeseries or category.');
  }
  if (!hasText(spec.title)) errors.push('Chart title is required.');
  if (!spec.xAxis || !hasText(spec.xAxis.key) || !hasText(spec.xAxis.type)) {
    errors.push('Chart xAxis requires an explicit key and type.');
  }
  if (!Array.isArray(spec.axes) || spec.axes.length === 0) {
    errors.push('Chart requires at least one explicit axis.');
  }
  if (!Array.isArray(spec.panes) || spec.panes.length === 0) {
    errors.push('Chart requires at least one explicit pane.');
  }
  if (!Array.isArray(spec.series) || spec.series.length === 0) {
    errors.push('Chart requires at least one explicit series.');
  }
  if (!Array.isArray(spec.data) || spec.data.length === 0) {
    errors.push('Chart requires data rows.');
  }
  if (errors.length > 0) return errors;

  const complete = spec as ChartSpec;
  const axisIds = new Set(complete.axes.map((axis) => axis.id));
  const paneIds = new Set(complete.panes.map((pane) => pane.id));
  const seriesIds = new Set<string>();
  const paneAxes = new Map(
    complete.panes.map((pane) => [pane.id, new Set(pane.axisIds)]),
  );

  complete.panes.forEach((pane) => {
    if (!hasText(pane.id) || !Number.isFinite(pane.weight) || pane.weight <= 0) {
      errors.push('Every pane requires an id and positive weight.');
    }
    pane.axisIds.forEach((axisId) => {
      if (!axisIds.has(axisId)) {
        errors.push(`Pane ${pane.id} references unknown axis ${axisId}.`);
      }
    });
  });

  complete.series.forEach((series: ChartSeries) => {
    if (!hasText(series.id) || !MARKS.has(series.mark)) {
      errors.push('Every series requires an id and supported mark.');
    }
    if (seriesIds.has(series.id)) {
      errors.push(`Series id ${series.id} is duplicated.`);
    }
    seriesIds.add(series.id);
    if (!paneIds.has(series.paneId)) {
      errors.push(`Series ${series.id} references unknown pane ${series.paneId}.`);
    }
    if (!axisIds.has(series.axisId) || !paneAxes.get(series.paneId)?.has(series.axisId)) {
      errors.push(
        `Series ${series.id} axis ${series.axisId} is not assigned to pane ${series.paneId}.`,
      );
    }
    if (series.mark === 'candlestick') {
      const mapping = series.fieldMapping;
      if (!mapping?.open || !mapping.high || !mapping.low || !mapping.close) {
        errors.push(
          `Candlestick series ${series.id} requires open, high, low, and close mappings.`,
        );
      } else {
        Object.values(mapping).forEach((key) => {
          if (!numericFieldExists(complete, key)) {
            errors.push(`Candlestick series ${series.id} field ${key} has no numeric values.`);
          }
        });
      }
    } else if (!series.dataKey) {
      errors.push(`Series ${series.id} requires an explicit dataKey.`);
    } else if (!numericFieldExists(complete, series.dataKey)) {
      errors.push(`Series ${series.id} field ${series.dataKey} has no numeric values.`);
    }
  });

  complete.data.forEach((row, index) => {
    if (!(complete.xAxis.key in row)) {
      errors.push(`Data row ${index + 1} is missing x-axis field ${complete.xAxis.key}.`);
    }
  });

  (complete.referenceLines || []).forEach((line) => {
    if (!paneIds.has(line.paneId) || !paneAxes.get(line.paneId)?.has(line.axisId)) {
      errors.push(`Reference line ${line.id} has an invalid pane or axis.`);
    }
    const hasStart = line.xStart !== undefined;
    const hasEnd = line.xEnd !== undefined;
    if (hasStart !== hasEnd) {
      errors.push(`Reference line ${line.id} requires both xStart and xEnd.`);
    }
    if (
      hasStart &&
      hasEnd &&
      !complete.data.some((row) => row[complete.xAxis.key] === line.xStart)
    ) {
      errors.push(`Reference line ${line.id} xStart is not present in chart data.`);
    }
    if (
      hasStart &&
      hasEnd &&
      !complete.data.some((row) => row[complete.xAxis.key] === line.xEnd)
    ) {
      errors.push(`Reference line ${line.id} xEnd is not present in chart data.`);
    }
  });
  (complete.markers || []).forEach((marker) => {
    if (!paneIds.has(marker.paneId) || !seriesIds.has(marker.seriesId)) {
      errors.push(`Marker ${marker.id} has an invalid pane or series.`);
    }
  });

  return errors;
}

export function chartGeometry(spec: ChartSpec): ChartGeometry {
  return {
    kind: spec.kind,
    xAxis: spec.xAxis,
    axes: spec.axes.map(({ id, side, format, domain }) => ({
      id,
      side,
      format,
      domain,
    })),
    panes: spec.panes.map(({ id, weight, axisIds }) => ({
      id,
      weight,
      axisIds,
    })),
    series: spec.series.map(
      ({ id, semanticElementId, mark, paneId, axisId, dataKey, fieldMapping }) => ({
        id,
        semanticElementId,
        mark,
        paneId,
        axisId,
        dataKey,
        fieldMapping,
      }),
    ),
    referenceLines: (spec.referenceLines || []).map(
      ({ id, semanticElementId, paneId, axisId, value, unit, xStart, xEnd }) => ({
        id,
        semanticElementId,
        paneId,
        axisId,
        value,
        unit,
        xStart,
        xEnd,
      }),
    ),
    markers: (spec.markers || []).map(
      ({ id, semanticElementId, paneId, seriesId, x, value, unit, placement }) => ({
        id,
        semanticElementId,
        paneId,
        seriesId,
        x,
        value,
        unit,
        placement,
      }),
    ),
  };
}
